import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import { ViewRequest, getEligibilityBasedQueryKey, getViewRequest, request } from "./request";
import { AuthenticationContext } from "../context";

export interface PharmacyServices {
  readonly accepts_eprescriptions_indicator: string;
  readonly delivery_service_indicator: string;
  readonly compounding_service_indicator: string;
  readonly drive_up_window_indicator: string;
  readonly durable_medical_equipment_indicator: string;
  readonly walk_in_clinic_indicator: string;
  readonly _24_hour_emergency_service_indicator: string;
  readonly multi_dose_compliance_packaging_indicator: string;
  readonly immunizations_provided_indicator: string;
  readonly handicapped_accessible_indicator: string;
  readonly _340b_status_indicator: string;
  readonly closed_door_facility_indicator: string;
}

export interface Pharmacy {
  readonly provider_id: string;
  readonly npi: string;
  readonly name?: string;
  readonly address_line_1?: string;
  readonly address_line_2?: string;
  readonly city?: string;
  readonly state?: string;
  readonly zip?: string;
  readonly latitude?: string;
  readonly longitude?: string;
  readonly is_24_hours?: "Y" | "N";
  readonly services?: PharmacyServices;
  readonly hours?: string[];
  readonly network_status?: string;
  readonly phone?: string;
  readonly languages?: string[];
  readonly bounding_box?: string;
  readonly all_pharmacies?: "Y" | "N";
  readonly distance?: number;
  readonly minority_owned_pharmacy?: "Y" | "N";
  readonly source_network_ids?: number[];
  readonly pharmacy_display_rules?: {
    readonly display_tag?: string;
    readonly description?: string;
    readonly display_on_map?: string;
  };
  readonly pharmacy_lookup_rule_id?: number;
  readonly is_medicaid_enrolled?: string;
}

export const getPharmacy = async (authToken: string, body: { readonly provider_id: string } & Partial<ViewRequest>) => {
  return request<typeof body, Pharmacy>({
    endpoint: "/pharmacy/get",
    body: { ...body },
  }, authToken);
};

export const useGetPharmacyQuery = (providerId: string, enabled?: boolean) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getPharmacy(
          await getAuthToken(),
          {
            provider_id: providerId,
            ...getViewRequest(memberEligibility),
          });
      } else {
        return getPharmacy(await getAuthToken(), { provider_id: providerId });
      }
    },
    queryKey: ["pharmacyGet", providerId, ...getEligibilityBasedQueryKey(memberEligibility)],
    enabled: enabled,
    staleTime: 500,
    retry: false,
  });

  return query;
};

export interface ListPharmaciesRequest {
  readonly longitude: string;
  readonly latitude: string;
  readonly page_number?: number;
  readonly results_per_page?: number;
  readonly bounding_box?: {
    readonly nw: string;
    readonly se: string;
    readonly sw: string;
    readonly ne: string;
  };
  readonly favorite_pharmacy_provider_ids_display_first?: string[];
  readonly ignore_custom_rules?: boolean;
}

export interface ListPharmaciesResponse {
  readonly results: { readonly pharmacy: Pharmacy }[];
  readonly result_count: string;
}

export const listPharmacies = async (authToken: string, body: ListPharmaciesRequest & Partial<ViewRequest>) => {
  return request<typeof body, ListPharmaciesResponse>({
    endpoint: "/pharmacy/list",
    body: { ...body },
  }, authToken);
};

export const useListPharmaciesQuery = (params: ListPharmaciesRequest, enabled = true) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["pharmacyList"];

  if (params?.latitude) {
    queryKey.push(params.latitude);
  }

  if (params?.longitude) {
    queryKey.push(params.longitude);
  }

  if (params?.page_number) {
    queryKey.push(params.page_number.toString());
  }

  if (params?.results_per_page) {
    queryKey.push(params.results_per_page.toString());
  }

  if (params?.favorite_pharmacy_provider_ids_display_first) {
    queryKey.push(params.favorite_pharmacy_provider_ids_display_first.toString());
  }

  if (params?.ignore_custom_rules) {
    queryKey.push(params.ignore_custom_rules ? "ignore_rules" : "do_not_ignore_rules");
  }

  queryKey.push(...getEligibilityBasedQueryKey(memberEligibility));

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listPharmacies(
          await getAuthToken(),
          {
            ...params,
            ...getViewRequest(memberEligibility),
          });
      } else {
        return listPharmacies(await getAuthToken(), params);
      }
    },
    queryKey: queryKey,
    enabled: enabled,
    retry: false,
  });

  return query;
};

export const listFavoritePharmacies = async (authToken: string, body?: Partial<ViewRequest>) => {
  return request<typeof body, ListPharmaciesResponse>({
    endpoint: "/favorite_pharmacy/list",
    body: { ...(body || {}) },
  }, authToken);
};

export const useListFavoritePharmaciesQuery = (enabled = true) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listFavoritePharmacies(
          await getAuthToken(),
          { ...getViewRequest(memberEligibility) },
        );
      } else {
        return listFavoritePharmacies(await getAuthToken());
      }
    },
    queryKey: ["favoritePharmacyList", ...getEligibilityBasedQueryKey(memberEligibility)],
    enabled: enabled === true,
    staleTime: Infinity,
  });

  return query;
};

export const updateFavoritePharmacy = async (authToken: string, providerId: string) => {
  return request<{ readonly provider_id: string }, never>({
    endpoint: "/favorite_pharmacy/update",
    body: { provider_id: providerId },
  }, authToken);
};

export const useUpdateFavoritePharamcyMutation = () => {
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (providerId: string) => {
      return updateFavoritePharmacy(await getAuthToken(), providerId);
    },
    mutationKey: ["updateFavoritePharmacy"],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["favoritePharmacyList", ...getEligibilityBasedQueryKey(memberEligibility)],
      });
    },
  });

  return mutation;
};

export const createFavoritePharmacy = (authToken: string, providerId: string) => {
  return request<{ readonly provider_id: string }, Pharmacy>({
    endpoint: "/favorite_pharmacy/create",
    body: { provider_id: providerId },
  }, authToken);
};

export const useCreateFavoritePharmacyMutation = () => {
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (providerId: string) => {
      return createFavoritePharmacy(await getAuthToken(), providerId);
    },
    mutationKey: ["createFavoritePharmacy"],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["favoritePharmacyList", ...getEligibilityBasedQueryKey(memberEligibility)],
      });
    },
  });

  return mutation;
};

export interface LocationGeolocate {
  readonly address?: string;
  readonly search_address?: string;
  readonly latitude: string;
  readonly longitude: string;
  readonly zip_code?: string;
}

export const locationGeolocate = async (authToken: string, body: Partial<LocationGeolocate>) => {
  return request<Partial<LocationGeolocate>, LocationGeolocate>({
    endpoint: "/location/geolocate",
    body: { ...body },
  }, authToken);
};

export const useLocationGeolocateQuery = (filters: Partial<LocationGeolocate>, enabled = true) => {
  const queryKey = ["locationGeolocate"];
  const { getAuthToken } = useContext(AuthenticationContext);

  if (filters.address) {
    queryKey.push("address", filters.address);
  }
  if (filters.latitude) {
    queryKey.push("latitude", filters.latitude);
  }
  if (filters.longitude) {
    queryKey.push("longitude", filters.longitude);
  }
  if (filters.search_address) {
    queryKey.push("searchAddress", filters.search_address);
  }
  if (filters.zip_code) {
    queryKey.push("zipCode", filters.zip_code);
  }

  const query = useQuery({
    queryFn: async () => {
      return locationGeolocate(await getAuthToken(), filters);
    },
    queryKey: queryKey,
    enabled: enabled === true,
  });

  return query;
};

export interface ListDMRPharmaciesRequest {
  readonly npi?: string;
  readonly name?: string;
  readonly address?: string;
  readonly zip?: string;
  readonly page_number?: number;
  readonly results_per_page?: number;
}

export const listDMRPharmacies = async (
  authToken: string,
  body: ListDMRPharmaciesRequest & Partial<ViewRequest>,
) => {
  return request<typeof body, ListPharmaciesResponse>({
    endpoint: "/dmr/pharmacy/list",
    body: { ...body },
  }, authToken);
};

export const useListDMRPharmaciesQuery = (
  params: ListDMRPharmaciesRequest,
  enabled: boolean = false,
) => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["pharmacyDMRList"];

  const query = useQuery({
    queryFn: async () => {
      return listDMRPharmacies(await getAuthToken(), params);
    },
    queryKey: queryKey,
    enabled: enabled,
    retry: false,
  });

  return query;
};
